import { Task } from "@common/types/task";

export interface AssigneeEstimateTotal {
    key: string;
    name: string;
    minutes: number;
}

export interface TasksEstimateTotalsData {
    totalMinutes: number;
    byAssignee: AssigneeEstimateTotal[];
}

const getAssigneeLabel = (task: Task): { key: string; name: string } => {
    if (task.assigneeId == null) {
        return { key: "unassigned", name: "Unassigned" };
    }

    return {
        key: String(task.assigneeId),
        name: task.assignee?.name ?? `User #${task.assigneeId}`,
    };
};

export const getSelectedTasksEstimateTotals = (
    tasks: Task[],
): TasksEstimateTotalsData | null => {
    const tasksWithEstimate = tasks.filter(
        (task): task is Task & { estimatedTime: number } =>
            task.estimatedTime != null && task.estimatedTime > 0,
    );

    if (tasksWithEstimate.length === 0) {
        return null;
    }

    const totalMinutes = tasksWithEstimate.reduce(
        (sum, task) => sum + task.estimatedTime,
        0,
    );

    const minutesByAssignee = new Map<string, AssigneeEstimateTotal>();

    for (const task of tasksWithEstimate) {
        const { key, name } = getAssigneeLabel(task);
        const existing = minutesByAssignee.get(key);
        if (existing) {
            existing.minutes += task.estimatedTime;
        } else {
            minutesByAssignee.set(key, {
                key,
                name,
                minutes: task.estimatedTime,
            });
        }
    }

    const byAssignee = [...minutesByAssignee.values()].sort((a, b) => {
        if (a.key === "unassigned") {
            return 1;
        }
        if (b.key === "unassigned") {
            return -1;
        }
        return b.minutes - a.minutes;
    });

    return { totalMinutes, byAssignee };
};
